import React, { useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Stack, TextInput, Group, LoadingOverlay, Container, Select } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import BreadcrumbsComponent from 'components/BreadcrumbsComponent';
import ModalPopupConfirmationComponent from 'components/ModalPopupConfirmationComponent';
import SecondaryButton from 'components/SecondaryButton';
import PrimaryButton from 'components/PrimaryButton';
import { masterUserAdminService } from 'services/dacum/masterUserAdminService';
import { masterAreaService } from 'services/dacum/masterAreaService';
import { CreateMasterUserAdminDto, EmplecUser } from 'services/api';
import PageTitle from 'components/PageTitle';
import { showErrorNotification, showSuccessNotification } from 'utils/notification';
import UserSelector from 'components/UserSelector';
import MultiAreaSelector from '../components/MultiAreaSelector';

const items = [
  { title: 'Master Admin User', href: '/app/dacum/master-user-admin' },
  { title: 'New Admin User', href: '' }
];

const DEPARTMENTS = [
  { value: 'Learning Solutions', label: 'Learning Solutions' },
  { value: 'Learning Operations', label: 'Learning Operations' },
  { value: 'Learning Partner', label: 'Learning Partner' },
  { value: 'Learning Technology and Experience', label: 'Learning Technology and Experience' }
];

const ROLES = [
  { value: 'Manager', label: 'Manager' },
  { value: 'General', label: 'General (Umum)' }
];

// Updated schema to handle array of area IDs
const userAdminSchema = z.object({
  adminId: z.string().min(1, 'Admin ID is required'),
  adminName: z.string().min(1, 'Admin name is required'),
  adminEmail: z.string().email('Valid email is required'),
  // Optional Beelajar email on form
  beelajarEmail: z.string().optional(),
  department: z.string().min(1, 'Department is required'),
  role: z.string().min(1, 'Role is required'),
  areaIds: z.array(z.string()).min(1, 'At least one area is required')
});

type FormData = z.infer<typeof userAdminSchema>;

const AddMasterUserAdminPage: React.FC = () => {
  const navigate = useNavigate();
  const [opened, { open, close }] = useDisclosure();
  const [formData, setFormData] = useState<FormData | null>(null);
  const [selectedUser, setSelectedUser] = useState<EmplecUser | null>(null);

  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors, isSubmitting }
  } = useForm<FormData>({
    resolver: zodResolver(userAdminSchema),
    defaultValues: {
      adminId: '',
      adminName: '',
      adminEmail: '',
      beelajarEmail: '',
      department: '',
      role: '',
      areaIds: []
    }
  });

  const { data: areasData, isLoading: isLoadingAreas } = useQuery({
    queryKey: ['masterAreas'],
    queryFn: () => masterAreaService.getList({ limit: 100 }),
    select: (response) => ({
      ...response,
      data: response.data.map((area: any) => ({
        value: area.areaID,
        label: `${area.areaCode} - ${area.areaName}`
      }))
    })
  });

  const createMutation = useMutation({
    mutationFn: (data: FormData) => {
      const createDto: CreateMasterUserAdminDto = {
        adminId: data.adminId,
        adminName: data.adminName,
        adminEmail: data.adminEmail,
        beelajarEmail: data.beelajarEmail || undefined,
        department: data.department,
        role: data.role,
        listArea: data.areaIds.map((areaId) => ({ areaId })) // Convert to required format
      };

      return masterUserAdminService.create(createDto);
    },
    onSuccess: () => {
      navigate('/app/dacum/master-user-admin');
      showSuccessNotification({
        title: 'Created Successfully!',
        message: 'Admin user has been created successfully'
      });
    },
    onError: (error: any) => {
      showErrorNotification({
        title: 'Failed to create admin user',
        message: error?.message || 'Something went wrong please try again'
      });
    }
  });

  const handleUserSelect = (user: EmplecUser | null) => {
    setSelectedUser(user);
    if (user) {
      setValue('adminId', user.binusianId);
      setValue('adminName', user.name);
      setValue('adminEmail', user.email);
      // Leave beelajarEmail empty for manual entry unless mapping exists
    } else {
      setValue('adminId', '');
      setValue('adminName', '');
      setValue('adminEmail', '');
      setValue('beelajarEmail', '');
    }
  };

  const onSubmit = (data: FormData) => {
    setFormData(data);
    open();
  };

  const processSubmit = () => {
    if (formData) {
      createMutation.mutate(formData);
    }
    close();
  };

  return (
    <Container fluid>
      <PageTitle title='Add Admin User' />
      <BreadcrumbsComponent items={items} />

      <ModalPopupConfirmationComponent
        buttonText='Submit'
        statusProcess='submit'
        opened={opened}
        open={open}
        close={close}
        onSubmit={processSubmit}
      />

      <div style={{ marginTop: '20px', position: 'relative' }}>
        <LoadingOverlay visible={isLoadingAreas || createMutation.isPending} />

        <form onSubmit={handleSubmit(onSubmit)}>
          <Stack gap='md'>
            <UserSelector
              label='Select User'
              placeholder='Search by name or Binusian ID...'
              required
              onChange={handleUserSelect}
              error={
                !selectedUser &&
                (errors.adminId?.message || errors.adminName?.message || errors.adminEmail?.message)
                  ? 'Please select a user'
                  : undefined
              }
            />

            <Controller
              name='adminId'
              control={control}
              render={({ field }) => (
                <TextInput
                  label='Admin ID (Binusian ID)'
                  placeholder='Will be auto-filled when user is selected'
                  error={errors.adminId?.message}
                  required
                  readOnly
                  variant='filled'
                  styles={{
                    input: {
                      backgroundColor: '#f5f5f5',
                      color: '#333'
                    }
                  }}
                  {...field}
                />
              )}
            />

            <Controller
              name='adminName'
              control={control}
              render={({ field }) => (
                <TextInput
                  label='Admin Name'
                  placeholder='Will be auto-filled when user is selected'
                  error={errors.adminName?.message}
                  required
                  readOnly
                  variant='filled'
                  styles={{
                    input: {
                      backgroundColor: '#f5f5f5',
                      color: '#333'
                    }
                  }}
                  {...field}
                />
              )}
            />

            <Controller
              name='adminEmail'
              control={control}
              render={({ field }) => (
                <TextInput
                  label='Admin Email'
                  placeholder='Will be auto-filled when user is selected'
                  error={errors.adminEmail?.message}
                  required
                  readOnly
                  variant='filled'
                  styles={{
                    input: {
                      backgroundColor: '#f5f5f5',
                      color: '#333'
                    }
                  }}
                  {...field}
                />
              )}
            />

            <Controller
              name='beelajarEmail'
              control={control}
              render={({ field }) => (
                <TextInput
                  label='Beelajar Email'
                  placeholder='Enter beelajar email (optional)'
                  error={errors.beelajarEmail?.message}
                  {...field}
                />
              )}
            />

            <Controller
              name='department'
              control={control}
              render={({ field }) => (
                <Select
                  label='Department'
                  placeholder='Select department'
                  data={DEPARTMENTS}
                  error={errors.department?.message}
                  required
                  {...field}
                />
              )}
            />

            <Controller
              name='role'
              control={control}
              render={({ field }) => (
                <Select
                  label='Role'
                  placeholder='Select role'
                  data={ROLES}
                  error={errors.role?.message}
                  required
                  {...field}
                />
              )}
            />

            <Controller
              name='areaIds'
              control={control}
              render={({ field }) => (
                <MultiAreaSelector
                  areas={areasData?.data || []}
                  value={field.value}
                  onChange={field.onChange}
                  loading={isLoadingAreas}
                  error={errors.areaIds?.message}
                  required
                />
              )}
            />
          </Stack>

          <Group justify='flex-end' mt='xl'>
            <SecondaryButton
              text='Cancel'
              onClick={() => navigate('/app/dacum/master-user-admin')}
              mr={10}
            />
            <PrimaryButton
              text='Save'
              type='submit'
              loading={isSubmitting || createMutation.isPending}
            />
          </Group>
        </form>
      </div>
    </Container>
  );
};

export default AddMasterUserAdminPage;
